import { useTranslation } from '@eti/providers';
import {
  MAX_PASSENGERS,
  MIN_ADULTS,
  MIN_CHILDREN,
  MIN_INFANTS,
} from '../../../../widgets/search-form/constants/passengers';
import OptionLabel from '../../components/edit-passengers/OptionLabel';
import { onAdultsCounterClick } from './adults';
import { generateChildrenAgesOptions, onChildrenCounterClick } from './Children';
import { onInfantsCounterClick } from './infants';

export const generateSelection = (adults, children, infants, childrenAges, infantsAges) => ({
  adults,
  children,
  infants,
  childrenAges,
  infantsAges,
});

export const isValidSelection = (adults, children, infants) =>
  adults + children <= MAX_PASSENGERS &&
  infants <= adults &&
  adults >= MIN_ADULTS &&
  children >= MIN_CHILDREN &&
  infants >= MIN_INFANTS;

const isPlusDisabled = (constraints, numberOfAdults, numberOfChildren) => {
  const maxNumberOfPassengers = constraints?.maxNumberOfTravelers || MAX_PASSENGERS;
  return numberOfAdults + numberOfChildren === maxNumberOfPassengers;
};

export const generateOptions = (
  constraints,
  numberOfAdults,
  numberOfChildren,
  numberOfInfants,
  childrenAges,
  infantsAges,
  onChange,
  { adultsLabel, childAgeInfo, childrenLabel, childAgeLabel, infantsLabel, infantsInformation },
) => [
  {
    dataTestId: `adults-option`,
    label: (
      <OptionLabel
        hasMinusDisabled={numberOfAdults === 1}
        hasPlusDisabled={isPlusDisabled(constraints, numberOfAdults, numberOfChildren)}
        id="adults"
        onMinusClick={onAdultsCounterClick(
          'subtract',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        onPlusClick={onAdultsCounterClick(
          'add',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        title={adultsLabel}
        value={numberOfAdults}
      />
    ),
  },
  {
    dataTestId: `children-option`,
    label: (
      <OptionLabel
        hasMinusDisabled={numberOfChildren === 0}
        hasPlusDisabled={isPlusDisabled(constraints, numberOfAdults, numberOfChildren)}
        id="children"
        onMinusClick={onChildrenCounterClick(
          'subtract',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        onPlusClick={onChildrenCounterClick(
          'add',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        title={childrenLabel}
        value={numberOfChildren}
      />
    ),
  },
  {
    dataTestId: `infants-option`,
    label: (
      <OptionLabel
        hasMinusDisabled={numberOfInfants === 0}
        hasPlusDisabled={
          numberOfInfants === numberOfAdults ||
          isPlusDisabled(constraints, numberOfAdults, numberOfChildren)
        }
        id="infants"
        onMinusClick={onInfantsCounterClick(
          'subtract',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        onPlusClick={onInfantsCounterClick(
          'add',
          numberOfAdults,
          numberOfChildren,
          numberOfInfants,
          childrenAges,
          infantsAges,
          onChange,
        )}
        subTitle={infantsInformation}
        title={infantsLabel}
        value={numberOfInfants}
      />
    ),
  },
  ...generateChildrenAgesOptions(
    numberOfAdults,
    numberOfChildren,
    numberOfInfants,
    childAgeInfo,
    childrenAges,
    infantsAges,
    onChange,
    childAgeLabel,
  ),
];

export const usePassengerOptions = (constraints, passengers, onChange) => {
  const { t } = useTranslation();

  const { adults, children, infants, childrenAges, infantsAges } = passengers;

  return generateOptions(
    constraints,
    adults,
    children,
    infants,
    childrenAges,
    infantsAges,
    onChange,

    {
      adultsLabel: t('Air.Search.Adults'),
      childAgeInfo: t('Responsive.Air.SearchFormPage.PassengersPane.ChildAgeInfo'),
      childrenLabel: t('Air.Search.Children'),
      childAgeLabel: t('Air.Search.ChildAge'),
      infantsLabel: t('Air.Search.Infants'),
      infantsInformation: t('Searchform.Passengers.Infants.Information'),
    },
  );
};
